import { useState, useCallback, useMemo, useRef } from 'react';
import { X } from 'lucide-react';

import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';

import { useSearchICD10Query, type ICD10CodeT } from '@/entities/labs';
import { formatIcd10CodeForDisplay } from '@/shared/lib/formatters';

export interface ICD10CodeSelectorProps {
  selectedCodes: ICD10CodeT[];
  onAdd: (code: ICD10CodeT) => void;
  onRemove: (code: string) => void;
}

export function ICD10CodeSelector({ selectedCodes, onAdd, onRemove }: ICD10CodeSelectorProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isFetching, isError } = useSearchICD10Query(search, open);
  const results = data?.codes ?? [];

  const selectedSet = useMemo(() => new Set(selectedCodes.map((c) => c.code)), [selectedCodes]);

  const handleSelect = useCallback(
    (code: ICD10CodeT) => {
      if (!selectedSet.has(code.code)) {
        onAdd(code);
      }
      setSearch('');
    },
    [onAdd, selectedSet]
  );

  return (
    <div className="space-y-2">
      <Command
        className="relative overflow-visible bg-transparent [&_[data-slot=command-input-wrapper]]:rounded-full [&_[data-slot=command-input-wrapper]]:border [&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-list]]:absolute [&_[data-slot=command-list]]:inset-x-0 [&_[data-slot=command-list]]:top-full [&_[data-slot=command-list]]:z-10 [&_[data-slot=command-list]]:mt-1 [&_[data-slot=command-list]]:rounded-md [&_[data-slot=command-list]]:border [&_[data-slot=command-list]]:bg-popover [&_[data-slot=command-list]]:shadow-md"
        shouldFilter={false}
      >
        <CommandInput
          ref={inputRef}
          placeholder="Search ICD-10 codes…"
          value={search}
          onValueChange={(v) => {
            setSearch(v);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            setTimeout(() => setOpen(false), 200);
          }}
        />
        {open && search.trim().length >= 2 && (
          <CommandList>
            {isError ? (
              <div className="px-3 py-2 text-center text-xs text-destructive">
                Diagnosis search is unavailable. Try again.
              </div>
            ) : isFetching && results.length === 0 ? (
              <div className="px-3 py-2 text-center text-xs text-muted-foreground">Searching…</div>
            ) : results.length === 0 ? (
              <CommandEmpty>No matching codes found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((code) => {
                  const isSelected = selectedSet.has(code.code);
                  return (
                    <CommandItem
                      key={code.code}
                      value={code.code}
                      onSelect={() => handleSelect(code)}
                      disabled={isSelected}
                      className={isSelected ? 'opacity-50' : undefined}
                    >
                      <span className="min-w-[60px] font-mono text-xs font-semibold">
                        {formatIcd10CodeForDisplay(code.code)}
                      </span>
                      <span className="truncate text-sm">{code.description}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        )}
      </Command>

      {selectedCodes.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedCodes.map((code) => (
            <Badge key={code.code} variant="secondary" className="gap-1 pr-1">
              <span className="font-semibold">{formatIcd10CodeForDisplay(code.code)}</span>
              <span className="max-w-[180px] truncate text-xs opacity-80">{code.description}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-0.5 size-4 p-0 hover:bg-transparent"
                onClick={() => onRemove(code.code)}
                aria-label={`Remove ${formatIcd10CodeForDisplay(code.code)}`}
              >
                <X className="size-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Type at least 2 characters to search. Select one or more diagnosis codes.
      </p>
    </div>
  );
}
